/* eslint-disable no-undef */
/**
 * HTTP client for the Lawn Irrigation Dashboard API.
 *
 * SEPARATION CONTRACT
 * -------------------
 * Every call here targets `/api/lawn/*` and nothing else. This file deliberately
 * does not import the greenhouse card/action hooks so the lawn product can be
 * pointed at its own service by changing `LAWN_SERVER` alone.
 */

import { fetchWithTimeout } from './apiUtils';

/**
 * Base URL for the lawn API.
 *
 * Prefers an explicit `REACT_APP_LAWN_SERVER`. Otherwise it derives the path
 * from the existing cards server, which today is the same Flask app.
 */
const LAWN_SERVER =
  process.env.REACT_APP_LAWN_SERVER ||
  (process.env.REACT_APP_CARDS_SERVER || '').replace(/\/api\/cards\/?$/i, '/api/lawn/');

export const isLawnApiConfigured = () => Boolean(LAWN_SERVER);

const buildUrl = (path) => `${LAWN_SERVER}${path}`;

/** Thrown for any non-2xx lawn API response, carrying the HTTP status. */
export class LawnApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'LawnApiError';
    this.status = status;
  }
}

const parseResponse = async (response, operation) => {
  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.error || body?.details || '';
    } catch {
      // Non-JSON error body; the status alone is enough context.
    }
    throw new LawnApiError(
      detail || `${operation} failed with status ${response.status}`,
      response.status
    );
  }

  if (response.status === 204) {
    return null;
  }

  try {
    return await response.json();
  } catch {
    return null;
  }
};

const request = async (path, { method = 'GET', body, operation, timeout = 12000 } = {}) => {
  if (!LAWN_SERVER) {
    throw new LawnApiError('Lawn API is not configured', 0);
  }

  const options = { method };
  if (body !== undefined) {
    options.headers = { 'Content-Type': 'application/json' };
    options.body = JSON.stringify(body);
  }

  const response = await fetchWithTimeout(buildUrl(path), options, timeout);
  return parseResponse(response, operation || `${method} ${path}`);
};

const encode = (value) => encodeURIComponent(value);

/** GET the whole dashboard in one call: property, sensors, forecast, ordinance, layout. */
export const fetchLawnDashboard = (username, propertyId) => {
  const query = propertyId ? `?propertyId=${encode(propertyId)}` : '';
  return request(`dashboard/${encode(username)}${query}`, { operation: 'fetchLawnDashboard' });
};

export const patchLawnProperty = (username, propertyId, updates) =>
  request(`properties/${encode(username)}/${encode(propertyId)}`, {
    method: 'PATCH',
    body: updates,
    operation: 'patchLawnProperty',
  });

export const createLawnSensor = (username, sensor) =>
  request(`sensors/${encode(username)}`, {
    method: 'POST',
    body: sensor,
    operation: 'createLawnSensor',
  });

export const patchLawnSensor = (username, sensorId, updates) =>
  request(`sensors/${encode(username)}/${encode(sensorId)}`, {
    method: 'PATCH',
    body: updates,
    operation: 'patchLawnSensor',
  });

export const deleteLawnSensor = (username, sensorId) =>
  request(`sensors/${encode(username)}/${encode(sensorId)}`, {
    method: 'DELETE',
    operation: 'deleteLawnSensor',
  });

export const fetchLawnSensorReadings = (username, sensorId) =>
  request(`sensors/${encode(username)}/${encode(sensorId)}/readings`, {
    operation: 'fetchLawnSensorReadings',
  });

export const fetchLawnForecast = (username, propertyId) =>
  request(`forecast/${encode(username)}/${encode(propertyId)}`, {
    operation: 'fetchLawnForecast',
  });

export const fetchLawnOrdinance = (username, propertyId) =>
  request(`ordinance/${encode(username)}/${encode(propertyId)}`, {
    operation: 'fetchLawnOrdinance',
  });

export const saveLawnWidgets = (username, widgets) =>
  request(`widgets/${encode(username)}`, {
    method: 'PUT',
    body: { widgets },
    operation: 'saveLawnWidgets',
  });

export const resetLawnDashboard = (username) =>
  request(`reset/${encode(username)}`, { method: 'POST', operation: 'resetLawnDashboard' });
